import {
  HustlerProgramme,
  Intensity,
  Programme,
  ProgrammeDuration,
  StartProgrammeParams,
  StartedProgrammesFromLibrary,
  WorkoutEquipmentPiece,
} from '../dao/core-dao'
import { queryClient } from '../query-client'
import { coreService } from '../service/core-service'
import { queryKeys } from '../util/query-keys'
import { UseMutationResult, UseQueryOptions, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'

export const useProgrammes = (): UseQueryResult<Programme[], Error> => {
  return useQuery<Programme[], Error>(queryKeys.programmes(), () => coreService.getProgrammes(), {
    keepPreviousData: true,
  })
}

export const useProgramme = (programmeId: string): UseQueryResult<Programme, Error> => {
  return useQuery<Programme, Error>(queryKeys.programme(programmeId), () => coreService.getProgrammeById(programmeId), {
    enabled: !!programmeId,
    keepPreviousData: true,
  })
}

export const useMyStartedProgrammes = (
  options?: Omit<UseQueryOptions<HustlerProgramme[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<HustlerProgramme[], Error> => {
  return useQuery<HustlerProgramme[], Error>(queryKeys.myStartedProgrammes(), () => coreService.getMyStartedProgrammes(), {
    keepPreviousData: true,
    ...options,
  })
}

export const useMyStartedProgrammesFromLibrary = (
  params: { page: number; pageSize: number },
  options?: Omit<UseQueryOptions<StartedProgrammesFromLibrary, Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<StartedProgrammesFromLibrary, Error> => {
  const { page, pageSize } = params
  return useQuery<StartedProgrammesFromLibrary, Error>(
    queryKeys.myStartedProgrammesFromLibrary({ page, pageSize }),
    () => coreService.getMyStartedProgrammesFromLibrary({ page, pageSize }),
    {
      keepPreviousData: true,
      ...options,
    },
  )
}

export const useMyCompletedProgrammes = (
  options?: Omit<UseQueryOptions<HustlerProgramme[], Error>, 'queryKey' | 'queryFn'>,
): UseQueryResult<HustlerProgramme[], Error> => {
  return useQuery<HustlerProgramme[], Error>(queryKeys.myCompletedProgrammes(), () => coreService.getMyCompletedProgrammes(), {
    keepPreviousData: true,
    ...options,
  })
}

export const useStartProgramme = (): UseMutationResult<HustlerProgramme, Error, StartProgrammeParams> => {
  return useMutation<HustlerProgramme, Error, StartProgrammeParams>((params) => coreService.startProgramme(params), {
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.myStartedProgrammes())
      queryClient.invalidateQueries({ queryKey: queryKeys.myStartedProgrammesFromLibraryPrefix() })
      queryClient.invalidateQueries(queryKeys.suggestedSeries())
    },
  })
}

export const useProgrammeEquipment = (programmeId: string): UseQueryResult<WorkoutEquipmentPiece[], Error> => {
  return useQuery<WorkoutEquipmentPiece[], Error>(
    queryKeys.programmeEquipment(programmeId),
    () => coreService.getProgrammeEquipmentList({ programmeId }),
    {
      enabled: !!programmeId,
      keepPreviousData: true,
    },
  )
}

export const useProgrammeDuration = (programmeId: string): UseQueryResult<ProgrammeDuration, Error> => {
  return useQuery<ProgrammeDuration, Error>(
    queryKeys.programmeDuration(programmeId),
    () => coreService.getProgrammeDuration({ programmeId }),
    {
      enabled: !!programmeId,
      keepPreviousData: true,
    },
  )
}

export const useProgrammeIntensities = (): UseQueryResult<Intensity[], Error> => {
  return useQuery<Intensity[], Error>(queryKeys.programmeIntensities(), () => coreService.listProgrammeIntensities(), {
    keepPreviousData: true,
  })
}
